import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useTranslations } from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  paymentNotificationsService,
  type PaymentNotificationEvent,
  type PaymentNotificationPreferences,
  type PaymentNotificationPreferencesView,
} from "../services/paymentNotifications.service";

/** Every event the page can switch on; `breb_qr_payment_paid` has no producer yet. */
export const LIVE_PAYMENT_NOTIFICATION_EVENTS: PaymentNotificationEvent[] = [
  "payin_confirmed",
  "payin_rejected",
  "payout_paid",
  "payout_failed",
  "wallet_balance_low",
  "batch_pending_approval",
  "batch_finished",
  "breb_payin_confirmed",
  "breb_payout_paid",
  "breb_payout_failed",
];

// The backend's defaults: everything on except the low balance, which needs a threshold first.
const DEFAULT_PREFS: PaymentNotificationPreferencesView = {
  phone: null,
  default_phone: null,
  payin_confirmed: true,
  payin_rejected: true,
  payout_paid: true,
  payout_failed: true,
  wallet_balance_low: false,
  wallet_balance_low_thresholds: {},
  batch_pending_approval: true,
  batch_finished: true,
  breb_payin_confirmed: true,
  breb_payout_paid: true,
  breb_payout_failed: true,
  breb_qr_payment_paid: true,
};

const parsePositive = (raw: string | undefined): number | null => {
  const parsed = Number.parseFloat((raw ?? "").trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toInputs = (thresholds: Record<string, number>): Record<string, string> =>
  Object.fromEntries(Object.entries(thresholds).map(([currency, value]) => [currency, String(value)]));

/**
 * The organization's payment notification preferences, as the page consumes them. Mirrors the cards hook:
 * switching one ON is confirmed first, switching one off is immediate, writes are optimistic.
 */
export function usePaymentNotificationPreferences() {
  const { t } = useTranslations("notificationPreferences");
  const { t: tWallets } = useTranslations("wallets");
  const { user, can } = useAuth();
  const organizationId = user?.organization_id;
  const canWrite = can("payments:write");

  const [prefs, setPrefs] = useState<PaymentNotificationPreferencesView>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<PaymentNotificationEvent | null>(null);
  const [thresholdInputs, setThresholdInputs] = useState<Record<string, string>>({});
  const [thresholdError, setThresholdError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    paymentNotificationsService
      .get(organizationId)
      .then((next) => {
        if (cancelled) return;
        setPrefs(next);
        setThresholdInputs(toInputs(next.wallet_balance_low_thresholds));
      })
      .catch((error: unknown) => {
        if (!cancelled && HttpApiService.isAccessDeniedError(error)) setHasNoAccess(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const applyPatch = useCallback(
    async (patch: Partial<PaymentNotificationPreferences>): Promise<boolean> => {
      if (!organizationId || isSaving) return false;
      const previous = prefs;
      setPrefs((p) => ({ ...p, ...patch }));
      setIsSaving(true);
      try {
        const updated = await paymentNotificationsService.update(organizationId, patch);
        setPrefs(updated);
        colocaToast.success(t("update_success"));
        return true;
      } catch (error) {
        setPrefs(previous);
        colocaToast.error(HttpApiService.getErrorMessage(error));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [organizationId, isSaving, prefs, t],
  );

  const parsedThresholds = (): Record<string, number> =>
    Object.fromEntries(
      Object.entries(thresholdInputs)
        .map(([currency, raw]) => [currency, parsePositive(raw)] as const)
        .filter((entry): entry is readonly [string, number] => entry[1] !== null),
    );

  const handleToggle = (event: PaymentNotificationEvent) => (next: boolean) => {
    if (!next) {
      void applyPatch({ [event]: false });
      return;
    }
    // A low-balance alert with no threshold has nothing to compare against.
    if (event === "wallet_balance_low" && Object.keys(parsedThresholds()).length === 0) {
      setThresholdError(tWallets("notifications_threshold_required_error"));
      return;
    }
    setPendingToggle(event);
  };

  const confirmPendingToggle = async () => {
    if (!pendingToggle) return;
    const event = pendingToggle;
    setPendingToggle(null);
    if (event === "wallet_balance_low") {
      await applyPatch({ wallet_balance_low: true, wallet_balance_low_thresholds: parsedThresholds() });
      return;
    }
    await applyPatch({ [event]: true });
  };

  const handleThresholdChange = (currency: string, raw: string) => {
    setThresholdInputs((inputs) => ({ ...inputs, [currency]: raw }));
    setThresholdError(null);
  };

  const handleThresholdBlur = async () => {
    const raw = Object.values(thresholdInputs).filter((value) => value.trim() !== "");
    const thresholds = parsedThresholds();
    if (raw.length !== Object.keys(thresholds).length) {
      setThresholdError(tWallets("notifications_threshold_required_error"));
      return;
    }
    if (prefs.wallet_balance_low && Object.keys(thresholds).length === 0) {
      setThresholdError(tWallets("notifications_threshold_required_error"));
      return;
    }
    setThresholdError(null);
    const stored = prefs.wallet_balance_low_thresholds;
    const changed =
      Object.keys(thresholds).length !== Object.keys(stored).length ||
      Object.entries(thresholds).some(([currency, value]) => stored[currency] !== value);
    if (changed) await applyPatch({ wallet_balance_low_thresholds: thresholds });
  };

  /** `""` clears it back to `default_phone`. */
  const saveRecipient = (phone: string) => applyPatch({ phone: phone === "" ? null : phone });

  /** Sends one sample of `event`; resolves `false` when it did not go out, after saying why. */
  const sendTest = (event: PaymentNotificationEvent) => async (phone: string) => {
    if (!organizationId) return false;
    try {
      const result = await paymentNotificationsService.sendTest(organizationId, event, phone);
      if (result.status === "skipped") {
        colocaToast.warning(t("send_test_skipped"));
        return false;
      }
      colocaToast.success(t("send_test_success"));
      return true;
    } catch (error) {
      colocaToast.error(HttpApiService.getErrorMessage(error));
      return false;
    }
  };

  const activeCount = LIVE_PAYMENT_NOTIFICATION_EVENTS.filter((event) => prefs[event]).length;

  return {
    prefs,
    isLoading,
    hasNoAccess,
    canWrite,
    controlsDisabled: isLoading || isSaving || !organizationId || !canWrite,
    activeCount,
    totalTypes: LIVE_PAYMENT_NOTIFICATION_EVENTS.length,
    pendingToggle,
    setPendingToggle,
    confirmPendingToggle,
    handleToggle,
    thresholdInputs,
    thresholdError,
    handleThresholdChange,
    handleThresholdBlur,
    saveRecipient,
    sendTest,
  };
}
